import { Component, computed, inject, signal, type WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { finalize } from 'rxjs';
import type { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { CardHeader } from "../../../shared/components/card/card-header/card-header";
import { FormBuilder, ReactiveFormsModule, Validators, type FormGroup } from '@angular/forms';
import { Message } from "primeng/message";
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop'; // <- Importante!
import { generatePassword } from '../../../shared/functions/password';
import { UserService } from '../../../services/user.service';
import { Brand, FaviconUrl, SystemTitle } from '../../../interfaces/application';
import { LayoutService } from '../../../services/layout.service';

@Component({
  selector: 'app-user-form-finalize',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardHeader,
    Message,
    InputTextModule,
    InputMaskModule,
    PasswordModule,
    ButtonModule
],
  templateUrl: './user-form-finalize.html',
  styleUrl: './user-form-finalize.css'
})
export class UserFormFinalize {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _router: Router = inject(Router);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _userService: UserService = inject(UserService);
  private readonly _layoutService: LayoutService = inject(LayoutService);

  protected brand: Brand = Brand.LOGAME;

  public code: WritableSignal<string> = signal<string>('');
  public isValidCode: WritableSignal<boolean> = signal<boolean>(false);
  public isUserCompleted: WritableSignal<boolean> = signal<boolean>(false);

  public loading: WritableSignal<boolean> = signal<boolean>(false);

  public form: FormGroup = this._fb.group({
    email: [{value: null, disabled: true}, [Validators.required]],
    name: [null, [Validators.required]],
    password: [null, [Validators.required]],
    phone: [null, [Validators.required]],
    phone_code: [null],
    phone_code_valid: [false, [Validators.requiredTrue]],
    document: [null, [Validators.required]],
  });

  public password = toSignal(this.form.controls['password'].valueChanges, {
    initialValue: this.form.controls['password'].value ?? '',
  });

  public hasMinLength = computed(() => this.password()?.length >= 8);
  public hasUpperAndLower = computed(() => /[A-Z]/.test(this.password()) && /[a-z]/.test(this.password()));
  public hasNumber = computed(() => /\d/.test(this.password()));
  public hasSpecialChar = computed(() => /[!@#$%^&*()_\-+={}[\]|:;"'<>,.?/~`]/.test(this.password()));

  public passwordValidators = [
    {
      valid: this.hasMinLength,
      message: 'A senha deve ter pelo menos 8 caracteres'
    },
    {
      valid: this.hasUpperAndLower,
      message: 'A senha deve conter pelo menos uma letra maiúscula e uma minúscula'
    },
    {
      valid: this.hasNumber,
      message: 'A senha deve conter pelo menos um número'
    },
    {
      valid: this.hasSpecialChar,
      message: 'A senha deve conter pelo menos um caractere especial'
    }
  ];

  public ngOnInit(): void {
    const { code } = this._activatedRoute.snapshot.params;
    this.code.set(code);

    this._checkCode(code);
  }

  private _toggleLoading(): void {
    this.loading.set(!this.loading());
  }

  private _checkCode(code: string): void {
    this._toggleLoading();
    this._authService.checkCode(code)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.isValidCode.set(true);

            this.form.patchValue(response.data)
            this.brand = response.data.brand;
            this._layoutService.initUpdateColors(response.data.brand);

            switch (response.data.brand) {
              case Brand.LOGAME:
                this._layoutService.changeFavicon(FaviconUrl.LOGAME);
                this._layoutService.setSystemTitle(SystemTitle.LOGAME);
                break;
              case Brand.B2XBET:
                this._layoutService.changeFavicon(FaviconUrl.B2XBET);
                this._layoutService.setSystemTitle(SystemTitle.B2XBET);
                break;
              case Brand.LIDERBET:
                this._layoutService.changeFavicon(FaviconUrl.LIDERBET);
                this._layoutService.setSystemTitle(SystemTitle.LIDERBET);
                break;
              case Brand.GERALBETR2M:
              case Brand.GERALBET:
                this._layoutService.changeFavicon(FaviconUrl.GERALBET);
                this._layoutService.setSystemTitle(SystemTitle.GERALBET);
                break;
            }
          }
        },
      })
  }

  public generateStrongPassword(): void {
    const password = generatePassword();
    this.form.patchValue({ password });
  }

  public sendSmsValidationCode(): void {
    const phone = this.form.get('phone')?.value;

    if (!phone) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Insira um número de telefone válido',
        life: 3000
      });

      return;
    }

    this._authService.sendSmsValidationCode(phone)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: 'Código de verificação enviado com sucesso',
              life: 3000
            });
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao enviar código de verificação',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public validCode(): void {
    const code = this.form.get('phone_code')?.value;

    if (!code) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Insira um código de seis dígitos',
        life: 3000
      });

      return;
    }

    this._authService.validCode(code)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: 'Código de verificação enviado com sucesso',
              life: 3000
            });

            this.form.patchValue({ phone_code_valid: true });
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao validar código de verificação',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsDirty();
      return;
    }

    this._toggleLoading();

    this._userService.finalize(this.code(), this.form.getRawValue())
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: 'Usuário finalizado com sucesso',
              life: 3000
            });

            this.isUserCompleted.set(true);
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao finalizar cadastro',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public goToLogin(): void {
    this._router.navigate(['auth', 'login']);
  }

}
